import { canonicalUrl } from "@/config/canonical";
import { HeroSection } from "@/containers/HeroSection";
import { FOLDOUT_HEADING } from "@/design-system/Foldout/Foldout";
import { InfoTip } from "@/design-system/InfoTip/InfoTip";
import { Skeleton } from "@/design-system/Skeleton/Skeleton";
import { TextButton } from "@/design-system/TextButton/TextButton";
import { useGoBack } from "@/hooks/useGoBack";
import { showModal } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import { userIsLoggedInSelector } from "@/redux/user/userSlice";
import {
  type GalleryCategory,
  type GalleryListSet,
  getGalleryCollectedCounts,
  getGallerySets,
} from "@repo/shared/api";
import { useTranslation } from "@repo/shared/i18n";
import { Lightbulb, TriangleAlert } from "lucide-react";
import { ReactNode, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  GalleryFirstOwnerSheet,
  StepNumber,
} from "./components/GalleryFirstOwnerSheet";

// The First Owner tag's tiers.
const BONUS_TIERS = [
  { range: "5-9", bonus: 150 },
  { range: "10-19", bonus: 300 },
  { range: "20+", bonus: 500 },
];
// Every collected card counts for it, so its count needs a word on which ones matter.
const STARTER_SET_ID = 116;
// The categories whose sets hold most historic First Owners.
const CATEGORY_SLUGS = ["leagues", "rarities"];

const Section = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <section className="flex flex-col gap-y-3">
    <h2 className="text-lg font-semibold">{title}</h2>
    {children}
  </section>
);

const Tip = ({ children }: { children: ReactNode }) => (
  <p className="flex items-start gap-x-2 text-foreground-muted">
    <Lightbulb className="mt-0.5 size-4 shrink-0" />
    <span>{children}</span>
  </p>
);

/** `/fut-gallery/update-first-owners`: why First Owners matter and how to mark the historic ones, set by set. */
export const GalleryFirstOwnersPage = () => {
  const { t } = useTranslation("gallery");
  const goBack = useGoBack({ fallback: "/fut-gallery/levels" });
  const isLoggedIn = useSelector(userIsLoggedInSelector);
  const dispatch = useDispatch<AppDispatch>();
  const [categories, setCategories] = useState<GalleryCategory[]>();
  const [loadFailed, setLoadFailed] = useState(false);
  const [openSet, setOpenSet] = useState<GalleryListSet>();
  useEffect(() => {
    let cancelled = false;
    getGallerySets()
      .then((result) => {
        if (!cancelled) setCategories(result);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  // How many collected players count for each set; signed out, none show.
  const [counts, setCounts] = useState<Map<number, number>>();
  useEffect(() => {
    if (!isLoggedIn) return;
    let cancelled = false;
    getGalleryCollectedCounts()
      .then((result) => {
        if (!cancelled)
          setCounts(new Map(result.map((c) => [c.setId, c.count])));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);
  // In gallery order, like the API sends them: category priority, then set priority.
  const shown =
    categories?.filter((c) => CATEGORY_SLUGS.includes(c.slug)) ?? [];

  return (
    <main className="z-10 h-full min-h-screen flex-1 bg-background text-white">
      <HeroSection
        helmetTitle={t("firstOwnersGuide.title")}
        pageTitle={t("firstOwnersGuide.title")}
        helmetCanonicalLink={canonicalUrl("/fut-gallery/update-first-owners")}
        back={goBack}
        shouldBeInTheDocumentFlow
      />
      <div className="mx-auto flex w-full max-w-[760px] flex-col gap-y-10 px-4 pb-12 pt-6 text-body text-foreground-body">
        <Section title={t("firstOwnersGuide.whyTitle")}>
          <p>{t("firstOwnersGuide.whyText")}</p>
          <table className="w-full max-w-sm overflow-hidden rounded-xl border border-neutral-700 bg-neutral-800 text-left tabular-nums">
            <thead className="text-xs uppercase text-neutral-400">
              <tr>
                <th className="px-4 py-2 font-semibold">
                  {t("firstOwnersGuide.tableFirstOwners")}
                </th>
                <th className="px-4 py-2 font-semibold">
                  {t("firstOwnersGuide.tableBonus")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-700 border-t border-neutral-700">
              {BONUS_TIERS.map(({ range, bonus }) => (
                <tr key={range}>
                  <td className="px-4 py-2">{range}</td>
                  <td className="px-4 py-2 font-semibold text-streamline-success">
                    +{bonus}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
        <Section title={t("firstOwnersGuide.extensionTitle")}>
          <p>{t("firstOwnersGuide.extensionSaved")}</p>
          <p>{t("firstOwnersGuide.extensionFuture")}</p>
          {/* Like the First Owners sheet's box, with the same text. */}
          <p className="flex items-start gap-x-2 rounded-lg border border-amber-500/50 bg-amber-900/30 p-3 font-semibold text-amber-200">
            <Lightbulb className="mt-px size-4 shrink-0 text-amber-400" />
            <span>{t("firstOwnersGuide.bestText")}</span>
          </p>
        </Section>
        <Section title={t("firstOwnersGuide.historicTitle")}>
          <p>{t("firstOwnersGuide.historicText")}</p>
          <p>{t("firstOwnersGuide.howIntro")}</p>
          <ol className="flex flex-col gap-y-3">
            <li className="flex items-start gap-x-2.5">
              <StepNumber n={1} className="mt-0.5" />
              <span>{t("firstOwnersGuide.step1")}</span>
            </li>
            <li className="flex items-start gap-x-2.5">
              <StepNumber n={2} className="mt-0.5" />
              <div className="flex flex-col gap-y-2">
                <span>{t("firstOwnersGuide.step2")}</span>
                <Tip>{t("firstOwnersGuide.tipMarkAll")}</Tip>
                <Tip>{t("firstOwnersGuide.tipSolution")}</Tip>
              </div>
            </li>
          </ol>
          {loadFailed ? (
            <p className="text-amber-400">{t("firstOwnersGuide.loadError")}</p>
          ) : !categories ? (
            <Skeleton className="h-64 w-full rounded-xl" />
          ) : (
            shown.map((category) => (
              <div key={category.id} className="mt-3 flex flex-col gap-y-2">
                <h3 className={FOLDOUT_HEADING}>{category.name}</h3>
                <ul className="flex flex-col divide-y divide-neutral-700 overflow-hidden rounded-xl border border-neutral-700 bg-neutral-800">
                  {category.sets.map((set) => (
                    <li
                      key={set.id}
                      className="flex items-center justify-between gap-x-3 px-4 py-2.5"
                    >
                      <span className="min-w-0 truncate">
                        <span className="font-semibold text-white">
                          {set.name}
                        </span>
                        {counts?.has(set.id) && (
                          <span className="ml-1.5 inline-flex items-center tabular-nums text-foreground-muted">
                            <span>
                              (
                              {t("firstOwnersGuide.playerCount", {
                                count: counts.get(set.id),
                              })}
                            </span>
                            <span>)</span>
                          </span>
                        )}
                      </span>
                      {/* The Starter Set's warning sits left of its link. */}
                      <span className="flex shrink-0 items-center gap-x-2">
                        {set.id === STARTER_SET_ID && (
                          <InfoTip
                            label={t("firstOwnersGuide.starterSetWarning")}
                            width={280}
                            icon={<TriangleAlert className="size-3.5" />}
                            className="text-amber-400 hover:text-amber-300"
                          >
                            {t("firstOwnersGuide.starterSetWarning")}
                          </InfoTip>
                        )}
                        <TextButton
                          size="medium"
                          className="text-blue-400 underline"
                          onClick={(e) => {
                            if (isLoggedIn) {
                              setOpenSet(set);
                              return;
                            }
                            // Else the modal's outside-click listener catches this same click.
                            e.stopPropagation();
                            dispatch(
                              showModal({ modalType: "login-required" }),
                            );
                          }}
                        >
                          {t("firstOwners.open")}
                        </TextButton>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </Section>
      </div>
      {/* Mounted only while open, like on the set cards. */}
      {openSet && (
        <GalleryFirstOwnerSheet
          setId={openSet.id}
          setName={openSet.name}
          isOpen
          onClose={() => setOpenSet(undefined)}
        />
      )}
    </main>
  );
};

export default GalleryFirstOwnersPage;
